import { useTranslation } from 'react-i18next';
import { UseSelectBasicObject } from '../../types/UseSelectBasicObject';
import { InputDatePicker } from '../InputDatePicker';
import { ModalType } from '../../hooks/useModal';

type ModalContentProps<T> = {
    type: ModalType;
    subtitle?: string;
    subtitle2?: string;
    countValues?: number;
    error?: string;
    endDate?: Date | null;
    onEndDateChange?: (date: Date | null) => void;
    startDate?: Date | null;
    onStartDateChange?: (date: Date | null) => void
    handleFileChange?: (file: File | null) => void;
    handleSelectChange?: (value: string) => void;
    handleMotiveDeleteChange?: (value: string) => void;
    optionsMap?: T[]; // Add optionsMap here
    handleAlertsPerson?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleCheckboxChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleSelectAllChange?: (checked: boolean) => void;
    showOptions?: boolean;
    selectedOptions?: string[];

};

export default function ModalContent<T extends UseSelectBasicObject>({ type, subtitle, subtitle2, countValues, error, endDate, onEndDateChange, handleFileChange, handleSelectChange, optionsMap, handleMotiveDeleteChange, showOptions, selectedOptions, handleCheckboxChange, handleSelectAllChange, }: ModalContentProps<T>) {
    const { t } = useTranslation(['common', 'alert', 'dashboard']);
    const renderContent = () => {
        switch (type) {
            case 'delete':
                return (
                    <div>
                        <label>{subtitle}</label>
                        <p>{subtitle2}</p>
                        <input
                            type="text"
                            onChange={(event) => {
                                handleMotiveDeleteChange?.(event.target.value);
                            }}
                            className="form-control"
                        />
                        <div>
                            {error && <small className="text-danger">{error}</small>}
                        </div>
                    </div>
                );
            case 'deleteWithoutInput':
                return (
                    <div>
                        <label>{subtitle}</label>
                        <p>{subtitle2}</p>
                        <div>
                            {error && <small className="text-danger">{error}</small>}
                        </div>
                    </div>
                );
            case "exportCsv":
                return (
                    <div>
                        {subtitle && <label>{subtitle}</label>}
                        {subtitle2 && <p>{subtitle2}</p>}
                        {error && <small className="text-danger">{error}</small>}
                        
                        {showOptions && optionsMap && optionsMap.length > 0 && (
                            <>
                                <div className="form-check form-switch mt-3 mb-2">
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        className="form-check-input"
                                        id="export-select-all"
                                        checked={optionsMap.every((field) => selectedOptions?.includes(String(field.id)))}
                                        onChange={(event) => handleSelectAllChange?.(event.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="export-select-all">
                                        {t("dashboard:csv.selectAllFields")}
                                    </label>
                                </div>
                                <div style={{ maxHeight: 320, overflowY: "auto" }}>
                                {optionsMap.map((field) => (
                                    <div className="form-check" key={field.id}>
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id={`export-field-${field.id}`}
                                            value={field.id}
                                            checked={selectedOptions?.includes(String(field.id)) ?? false}
                                            onChange={handleCheckboxChange}
                                        />
                                        <label className="form-check-label" htmlFor={`export-field-${field.id}`}>
                                            {field.name}
                                        </label>
                                    </div>
                                ))}
                                </div>
                            </>
                        )}
                    </div>
                );
            case 'end':
                console.log('end optionsMap:', optionsMap);
                return (
                    <div>
                        <div>
                            <InputDatePicker
                                value={endDate}
                                onChange={onEndDateChange}
                                label={subtitle}
                            />
                            {error && <small className="text-danger">{error}</small>}
                        </div>
                        {optionsMap && optionsMap.length > 0 && (
                            <div>
                                <label htmlFor="optionSelect" className="form-label">{subtitle2}</label>
                                <select
                                    id="optionSelect"
                                    className="form-control"
                                    onChange={(event) => {
                                        const selectedValue = event.target.value;
                                        handleSelectChange?.(selectedValue);
                                    }}
                                    defaultValue=""
                                >
                                    <option value="" disabled>{t("common:selectDefaultText")}</option>
                                    {optionsMap.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {item.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                );
            case 'upload':
                console.log('upload optionsMap:', optionsMap);
                return (
                    <div>
                        <div>
                            <label>{subtitle}</label>
                            <div>
                                <input
                                    type="file"
                                    onChange={(event) => {
                                        const file = event.target.files?.[0] || null;
                                        handleFileChange?.(file);
                                    }}
                                    accept=".csv,.xlsx,.xls"
                                    className="form-control"
                                />

                                {(countValues !== null && countValues !== undefined && countValues > 0) && <p><b>{t("participant:modal.participantModalHeaderCount", { countValues: countValues })}</b></p>}
                            </div>
                            {showOptions && (<div className="mt-3">
                                <label htmlFor="optionSelect" className="form-label">Select an option:</label>
                                <select
                                    id="optionSelect"
                                    className="form-control"
                                    onChange={(event) => {
                                        const selectedValue = event.target.value;
                                        handleSelectChange?.(selectedValue);
                                    }}
                                    defaultValue=""
                                >
                                    <option value="" disabled>Choose an option...</option>
                                    {optionsMap && optionsMap.length > 0 ? (
                                        optionsMap?.map((campaign) => (
                                            <option key={campaign.id} value={campaign.id}>
                                                {campaign.name}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="" disabled>No campaigns available</option>
                                    )}
                                </select>
                            </div>)}

                            {error && <div className="text-danger" style={{ fontSize: '1rem', marginTop: '10px' }}>{error}</div>}
                        </div>
                    </div>
                );
            case 'info':
                return <div><p>{subtitle}</p></div>;
            case 'restore':
            case 'active':
                return (
                    <div>
                        <label>{subtitle}</label>
                        <p>{subtitle2}</p>
                    </div>
                );
            default:
                return <div>Contenido no definido</div>;
        }
    };
    return <>{renderContent()}</>;
}